import { usePersonInfo } from '@/data/Content/PersonInfo';
import { useUser } from '@/data/User';
import { usePathname } from 'next/navigation';
import { useCurrentPhysicalStoreId } from 'presentation/events/hooks/useCurrentPhysicalStoreId';
import { useTrackPageView } from 'presentation/events/hooks/useTrackPageView';
import {
	SUBSCRIPTION_COMPLETE_PATH,
	useTrackSubscribe,
} from 'presentation/events/hooks/useTrackSubscribe';
import {
	clearGuestFacebookCAPIAttributes,
	saveFacebookCAPIUserAttributes,
} from 'presentation/events/utils/facebookUtils';
import { createExternalIdValue } from 'presentation/events/utils/pinterestUtils';
import { useEffect, useRef } from 'react';

/**
 * Custom React hook that centralizes the `page_view` and `subscribe` events for non-PDP/BDP
 * pages. Each fires independently, as soon as the data it needs is available:
 * - `page_view` needs user/personInfo.
 * - `subscribe` only needs the pathname to match the subscription-complete route.
 *
 * `page_load` for these pages lives separately in `useTrackGlobalPageLoad`, since it must
 * stay tied to the page actually finishing rendering, unlike the events here.
 */
export const useGlobalPageTrackEvents = () => {
	const pathname = usePathname();

	const { physicalStoreId } = useCurrentPhysicalStoreId();

	const { user, loading: isUserLoading } = useUser();
	const userId = user?.userId ?? null;
	const isLoggedIn = !!user?.isLoggedIn;

	const { personInfo, isLoading: isPersonInfoLoading } = usePersonInfo();

	const trackPageView = useTrackPageView();
	const trackSubscribeEvent = useTrackSubscribe();

	const pageViewTrackedKeyRef = useRef<string | null>(null);
	const subscribeTrackedPathRef = useRef<string | null>(null);

	useEffect(() => {
		if (!pathname) return;

		clearGuestFacebookCAPIAttributes({ pathname });
	}, [pathname]);

	// subscribe: only needs the pathname to match the subscription-complete route.
	useEffect(() => {
		if (!pathname?.includes(SUBSCRIPTION_COMPLETE_PATH)) return;
		if (subscribeTrackedPathRef.current === pathname) return;

		subscribeTrackedPathRef.current = pathname;
		trackSubscribeEvent();
	}, [pathname, trackSubscribeEvent]);

	// page_view: needs user/personInfo, not the page to finish rendering.
	useEffect(() => {
		if (!pathname) return;

		const isPDP = pathname.startsWith('/p/');
		const isBDP = pathname.startsWith('/g/');
		if (isPDP || isBDP) return;

		if (isUserLoading || isPersonInfoLoading) return;

		if (isLoggedIn) {
			saveFacebookCAPIUserAttributes({
				email: personInfo?.email1,
				phone: personInfo?.phone1,
				isGuest: false,
			});
		}

		const trackingKey = `${pathname}|${physicalStoreId}`;
		if (pageViewTrackedKeyRef.current === trackingKey) return;
		pageViewTrackedKeyRef.current = trackingKey;

		createExternalIdValue();

		trackPageView({
			userId,
			isLoggedIn,
			personInfo: {
				email: personInfo?.email1,
				firstName: personInfo?.firstName,
				lastName: personInfo?.lastName,
				phone: personInfo?.phone1,
				city: personInfo?.city,
				state: personInfo?.state,
				zipCode: personInfo?.zipCode,
			},
		});
	}, [
		pathname,
		physicalStoreId,
		userId,
		isLoggedIn,
		isUserLoading,
		isPersonInfoLoading,
		trackPageView,
		personInfo?.email1,
		personInfo?.firstName,
		personInfo?.lastName,
		personInfo?.phone1,
		personInfo?.city,
		personInfo?.state,
		personInfo?.zipCode,
	]);
};
